import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@noor/lib/cn';
import { buttonVariants } from './button';

/**
 * Icon-only sizing scale layered on top of the shared `buttonVariants`
 * (which is invoked with `size="icon"` baked in). Each handle maps the box to
 * the equivalent text-button height so an IconButton lines up next to a Button.
 */
const iconButtonSize = cva('', {
  variants: {
    size: {
      sm: 'size-9',
      default: 'size-12',
      lg: 'size-13',
    },
  },
  defaultVariants: {
    size: 'default',
  },
});

type IconButtonVariant = NonNullable<VariantProps<typeof buttonVariants>['variant']>;

type IconButtonProps = Omit<React.ComponentProps<'button'>, 'aria-label'> &
  VariantProps<typeof iconButtonSize> & {
    /** The accessible name for the icon-only control. Required for a11y. */
    'aria-label': string;
    variant?: IconButtonVariant;
    asChild?: boolean;
  };

function IconButton({
  className,
  variant = 'default',
  size,
  asChild = false,
  ...props
}: IconButtonProps) {
  const Comp = asChild ? Slot : 'button';
  return (
    <Comp
      data-slot="icon-button"
      className={cn(
        buttonVariants({ variant, size: 'icon' }),
        iconButtonSize({ size }),
        className,
      )}
      {...props}
    />
  );
}

export { IconButton };
export type { IconButtonProps };
